/** A delivered notification, as returned by the inbox API and pushed live over
 *  SignalR. Mirrors the backend `NotificationDto` (camelCase on the wire). */
export interface Notification {
  id: number;
  type: string;
  title: string;
  body: string;
  /** Frontend route/tab to open on click (e.g. "approvals"). */
  deepLink?: string | null;
  /** Type-specific payload as raw JSON; parse per `type`. */
  data?: string | null;
  read: boolean;
  createdAtUtc: string;
}

/** Inbox API response envelope (`ApiResponse<InboxResponse>.data`). */
export interface InboxResponse {
  items: Notification[];
  unreadCount: number;
}

/** `type` of a message sent by a skill's Notify node with channel = aiva_chat. */
export const SKILL_MESSAGE_TYPE = 'skill_message';

/** A status-only hub push: something an open pane shows has changed. It has no
 *  inbox row, so it never reaches the bell or a toast. */
export interface HubSignal {
  type: string;
  data?: string | null;
}

/** Signal `type` sent when an approval card changes state without its approvers
 *  being asked: another approver decided it, or its timeout resolved it. */
export const APPROVAL_CHANGED_SIGNAL = 'approval_changed';

/** Signal `type` sent to every pane when a Studio admin changes an AI connector or
 *  tier, so the connection dot re-checks at once. */
export const CONNECTION_CHANGED_SIGNAL = 'connection_changed';

/** `type` of a push raised for a parked approval: a gate parking, an escalation,
 *  or an approval that could not be applied. All of them change the Actions list. */
export const APPROVAL_NOTIFICATION_TYPE = 'approval';

/** Whether a live push concerns the approvals the Actions tab lists. The
 *  backend's "approvals" deep-link is accepted as well, so an older producer that
 *  only sets the link still triggers a refresh. */
export function isApprovalNotification(n: Pick<Notification, 'type' | 'deepLink'>): boolean {
  return n.type === APPROVAL_NOTIFICATION_TYPE || n.deepLink === 'approvals';
}

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';

/** Parsed `data` payload of a `skill_message` notification. */
export interface SkillMessageData {
  skillName: string;
  priority: NotificationPriority;
  /** The Notify node's suggestion buttons, cleaned and capped (may be empty). */
  quickReplies: string[];
}

/** A skill's text message, as the Actions tab lists it. */
export interface SkillMessage {
  /** The inbox row id — stable across reloads, so it keys the message's chat session. */
  notificationId: number;
  skillName: string;
  title: string;
  body: string;
  priority: NotificationPriority;
  quickReplies: string[];
  createdAtUtc: string;
  read: boolean;
}

const PRIORITIES: ReadonlySet<string> = new Set(['low', 'normal', 'high', 'urgent']);

/** Same cap as the backend notify tool (MAX_QUICK_REPLIES) and the thread seed. */
const MAX_QUICK_REPLIES = 5;
const QUICK_REPLY_MAX_CHARS = 80;

function parseQuickReplies(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const replies: string[] = [];
  for (const item of value) {
    if (typeof item !== 'string') continue;
    const text = item.trim().slice(0, QUICK_REPLY_MAX_CHARS);
    if (text && !replies.includes(text)) replies.push(text);
    if (replies.length === MAX_QUICK_REPLIES) break;
  }
  return replies;
}

/**
 * Parse a `skill_message` notification's `data`. Returns null for any other
 * type, or when `data` is missing or not valid JSON — the raw payload comes from
 * the backend as an opaque string, so it is never trusted to be well-formed.
 */
export function parseSkillMessageData(n: Notification): SkillMessageData | null {
  if (n.type !== SKILL_MESSAGE_TYPE || !n.data) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(n.data);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const skillName = typeof record.skillName === 'string' ? record.skillName.trim() : '';
  const priority =
    typeof record.priority === 'string' && PRIORITIES.has(record.priority)
      ? (record.priority as NotificationPriority)
      : 'normal';
  return { skillName, priority, quickReplies: parseQuickReplies(record.quickReplies) };
}

/** The Actions-tab view of a `skill_message` notification; null for any other. */
export function toSkillMessage(n: Notification): SkillMessage | null {
  const data = parseSkillMessageData(n);
  if (!data) return null;
  return {
    notificationId: n.id,
    skillName: data.skillName,
    title: n.title,
    body: n.body,
    priority: data.priority,
    quickReplies: data.quickReplies,
    createdAtUtc: n.createdAtUtc,
    read: n.read,
  };
}
